import fs from 'fs'
import path from 'path'

import { PROJECT_FILES_DIRECTORY, TESTING_FILES } from '../../constants'
import { packageRoot } from '../../templates/package-root'

// Copying the unit-test harness into a project.
//
// The harness is *copied*, not imported from the installed package, and that is the whole design. A test
// harness is something a project owns: a service wrapping an awkward SDK needs a shim inside the HTTP mock,
// and a project with its own conventions will want its own `jest.config.js`. A copy can be read and edited;
// a module behind a package export cannot. The cost is that a copy does not move with the CLI — which is
// why `init` is additive and safe to re-run, and why a project created before this existed picks the
// harness up by running `flowrunner init` again.
//
// Nothing is overwritten, for the same reason as everywhere else in `init`: a customer's edit to their own
// jest config must survive a command they ran for an unrelated reason.

export interface TestingFilesResult {
  created: string[]
  skipped: string[]
}

/** Where the shipped harness lives inside the installed package. */
export function testingFilesDirectory(): string {
  return path.join(packageRoot(), PROJECT_FILES_DIRECTORY)
}

/**
 * Copy the harness into `projectRoot`, leaving any file that is already there alone.
 *
 * Reports what it did per file so `init` can print the same `+ file` / `file — already there` lines it
 * prints for everything else it writes.
 */
export function writeTestingFiles(projectRoot: string): TestingFilesResult {
  const source = testingFilesDirectory()

  const created: string[] = []
  const skipped: string[] = []

  for (const file of TESTING_FILES) {
    const target = path.join(projectRoot, file)

    if (fs.existsSync(target)) {
      skipped.push(file)

      continue
    }

    const contents = fs.readFileSync(path.join(source, file))

    fs.mkdirSync(path.dirname(target), { recursive: true })
    fs.writeFileSync(target, contents)

    created.push(file)
  }

  return { created, skipped }
}
